import React, { useState } from 'react';
import { Image as ImageIcon } from 'lucide-react';
import type { BannerItem } from '../../services/resortAdminApi';

// B2C design tokens (Client App | B2C Figma) — the preview must mirror the feed card.
const PRIMARY = '#231f1b';
const SECONDARY = '#373734';
const ACCENT = '#f96400';
const SURFACE = '#f7f6f2';
// Client feed card reference width (PromoCarousel: 375pt screen − 16pt padding × 2).
// Text truncation only matches the app when the card renders at the same width
// with the same font, so don't let this stretch with the panel.
const REF_WIDTH = 343;
const INTER = "'Inter', ui-sans-serif, system-ui, -apple-system, sans-serif";

interface Props {
  banners: BannerItem[];
}

function bannerImage(b: BannerItem): string {
  return b.image_url || b.image || '';
}

/**
 * Live preview of the promo banner exactly as it renders in the B2C feed:
 * a 21:9 cover-cropped card with title / subtitle / CTA, manual light/dark text,
 * and pagination dots (only when there are 2+ banners).
 */
export const BannerPreviewCard: React.FC<Props> = ({ banners }) => {
  const [active, setActive] = useState(0);
  const list = banners.length ? banners : [];
  const idx = Math.min(active, Math.max(0, list.length - 1));
  const banner = list[idx];
  const isLight = banner?.text_theme === 'Light';
  const titleColor = isLight ? '#ffffff' : PRIMARY;
  const subtitleColor = isLight ? '#ffffff' : SECONDARY;
  const img = banner ? bannerImage(banner) : '';

  return (
    <div style={{ width: REF_WIDTH, maxWidth: '100%', fontFamily: INTER }}>
      <div
        className="relative w-full overflow-hidden rounded-3xl"
        style={{ aspectRatio: '21 / 9', backgroundColor: SURFACE }}
      >
        {img ? (
          <img src={img} alt="" className="absolute inset-0 h-full w-full object-cover" />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <ImageIcon className="h-10 w-10" style={{ color: '#cbd5e1' }} />
          </div>
        )}

        {/* Content overlay: title/subtitle top-left, CTA bottom-left */}
        <div className="absolute inset-0 flex flex-col justify-between px-4 pt-4 pb-4">
          <div className="max-w-[60%]">
            {banner?.title ? (
              <p
                className="truncate font-semibold"
                style={{ color: titleColor, fontSize: 16, lineHeight: '20px', letterSpacing: '-0.1px' }}
              >
                {banner.title}
              </p>
            ) : null}
            {banner?.description ? (
              <p
                className="truncate"
                style={{ color: subtitleColor, fontSize: 14, lineHeight: '20px' }}
              >
                {banner.description}
              </p>
            ) : null}
          </div>
          {banner?.button_text ? (
            <div className="flex">
              <span
                className="inline-flex h-8 items-center rounded-lg bg-white px-3 font-semibold"
                style={{ color: PRIMARY, fontSize: 14, lineHeight: '20px' }}
              >
                {banner.button_text}
              </span>
            </div>
          ) : null}
        </div>
      </div>

      {/* Pagination dots — only when 2+ banners */}
      {list.length > 1 && (
        <div className="mt-3 flex items-center justify-center gap-1">
          {list.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setActive(i)}
              className="rounded-full transition-all"
              style={{
                width: i === idx ? 16 : 8,
                height: 8,
                backgroundColor: ACCENT,
                opacity: i === idx ? 1 : 0.2,
              }}
              aria-label={`Preview banner ${i + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};
